import React                    from 'react';
import {
  Form,
  Field,
  useFormikContext,
}                               from 'formik';
import styled, { css }          from 'styled-components';
import { Grid, Button }         from '@material-ui/core';
import { Breakpoint }           from '@material-ui/core/styles/createBreakpoints';
import { IBannerAdValues }      from '@typings/nationalAdManagement';
import {
  Switch,
  TextInput,
  InputHint,
  RequiredLabel,
}                               from '@components/index';
import { Progress }             from '@components/Progress/Progress';
import { BackdropRadioGroup }   from '@modules/nationalAdManager/components/BackdropRadioGroup';
import { BannerItem }           from './BannerItem';

interface IStyledProps {
  selected: boolean;
}

const Wrapper = styled.div`
  width   : 100%;
  padding : 30px 30px 50px 30px;

  @media (min-width: 600px) {
    padding: 30px 60px 50px 60px;
  }

  @media (min-width: 960px) {
    padding: 30px 60px 50px 100px;
  }
`;

const ActionsWrapper = styled.div`
  display          : flex;
  justify-content  : flex-end;
  align-items      : center;
  margin           : 10px auto;

  button {
    margin: 0 10px;
  }

  ${({ theme: { width } }) => (width === 'xs' && css`
    flex-direction: column;

    button:first-child {
      margin-bottom: 20px;
    }
  `)};
`;

const ActiveTabsWrapper = styled.div`
  display : flex;
  margin  : 0 auto 25px;

  ${({ theme: { width } }) => ((width === 'xs' || width === 'sm') && 'width: fit-content;')};

  > div {
    width       : 140px;
    text-align  : center;
    white-space : nowrap;
    height      : 32px;
    padding-top : 6px;
    border      : 1px solid #0063A5;
  }

  div:first-child {
    border-radius: 5px 0 0 5px;
  }

  div:last-child {
    border-radius: 0 5px 5px 0;
  }
`;

const Tab = styled.div<IStyledProps>`
  cursor           : pointer;
  height           : 35px;
  padding-top      : 8px;
  padding-bottom   : 10px;
  color            : ${({ theme: { textColor }, selected }): string => (
    selected ? textColor.white : textColor.secondary)};
  background-color : ${({ theme: { backgroundColor }, selected }): string => (
    selected ? backgroundColor.darkBlue : backgroundColor.white)};
`;

const InputWrapperPadded = styled.div`
  padding: 8px 0 28px;
`;

const InputWrapper = styled.div`
  margin-bottom: 20px;

  textarea {
    color: ${({ theme: { textColor } }): string => textColor.darkGray};
  }
`;

const InputWrapperMargedTop = styled(InputWrapper)`
  @media (min-width: 1280px) {
    margin-top: 74px;
  }
`;

const ActionButton = styled(Button)`
  && {
    min-width : 160px;
    max-width : 335px;

    :disabled {
      background-color: ${({ theme: { backgroundColor } }): string => backgroundColor.gray};

      span {
        color: ${({ theme: { textColor } }): string => textColor.white};
      }
    }
  }
`;

interface IProps {
  width    : Breakpoint;
  onCancel : () => void;
}

export const BannerEditorForm: React.FC<IProps> = ({ width, onCancel }) => {
  const {
    values,
    touched,
    errors,
    isValid,
    isSubmitting,
    dirty,
    setFieldValue,
  } = useFormikContext<IBannerAdValues>();

  return (
    <Form>
      <Grid container>
        <Grid item xs={12} lg={8}>
          <Wrapper>
            <Grid container spacing={5}>
              <Grid item xs={12} md={7} lg={12}>
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <ActiveTabsWrapper>
                      <Tab
                        selected = {values.isActive}
                        onClick  = {(): void => setFieldValue('isActive', true)}
                      >
                        Ad Running
                      </Tab>
                      <Tab
                        selected = {!values.isActive}
                        onClick  = {(): void => setFieldValue('isActive', false)}
                      >
                        Suspend Ad
                      </Tab>
                    </ActiveTabsWrapper>

                    <BannerItem bannerData={values} />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <InputWrapperMargedTop>
                      <Field
                        id         = "titleText"
                        name       = "titleText"
                        type       = "text"
                        label      = "Title Text"
                        error      = {touched.titleText && !!errors.titleText}
                        component  = {TextInput}
                        inputProps = {{ maxLength: 40 }}
                      />
                      <InputHint
                        text  = "40 Character Max"
                        error = {touched.titleText && !!errors.titleText}
                      />
                      <RequiredLabel />
                    </InputWrapperMargedTop>

                    <InputWrapper>
                      <Field
                        id         = "subtext"
                        name       = "subtext"
                        type       = "text"
                        label      = "Sub Text"
                        error      = {touched.subtext && !!errors.subtext}
                        component  = {TextInput}
                        inputProps = {{ maxLength: 60 }}
                      />
                      <InputHint
                        text  = "60 Character Max"
                        error = {touched.subtext && !!errors.subtext}
                      />
                      <RequiredLabel />
                    </InputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <InputWrapperPadded>
                      <Field
                        id        = "isClickable"
                        name      = "isClickable"
                        label     = "Indicate if this ad will be clickable."
                        component = {Switch}
                      />
                    </InputWrapperPadded>

                    {values.isClickable && (
                      <>
                        <InputWrapper>
                          <Field
                            id         = "linkUrl"
                            name       = "linkUrl"
                            type       = "text"
                            label      = "Valid URL link"
                            error      = {touched.linkUrl && !!errors.linkUrl}
                            inputProps = {{ maxLength: 255 }}
                            component  = {TextInput}
                          />
                          <RequiredLabel />
                        </InputWrapper>

                        <InputWrapper>
                          <Field
                            id         = "buttonText"
                            name       = "buttonText"
                            type       = "text"
                            label      = "Button Text"
                            error      = {touched.buttonText && !!errors.buttonText}
                            component  = {TextInput}
                            inputProps = {{ maxLength: 15 }}
                          />
                          <InputHint
                            text  = "15 Character Max"
                            error = {touched.buttonText && !!errors.buttonText}
                          />
                          <RequiredLabel />
                        </InputWrapper>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {(width !== 'lg' && width !== 'xl') && (
                <Grid item xs={12} sm={5}>
                  <BackdropRadioGroup name="backdropType" />
                </Grid>
              )}
              <Grid item xs={12}>
                <ActionsWrapper>
                  <ActionButton
                    color     = "secondary"
                    variant   = "outlined"
                    onClick   = {onCancel}
                    fullWidth = {width === 'xs'}
                  >
                    Cancel
                  </ActionButton>

                  <ActionButton
                    type      = "submit"
                    color     = "secondary"
                    variant   = "contained"
                    disabled  = {!isValid || !dirty || isSubmitting}
                    fullWidth = {width === 'xs'}
                  >
                    Save
                    <Progress loading={isSubmitting} />
                  </ActionButton>
                </ActionsWrapper>
              </Grid>
            </Grid>
          </Wrapper>
        </Grid>

        {(width === 'lg' || width === 'xl') && (
          <Grid item xs={12} sm={4}>
            <BackdropRadioGroup name="backdropType" />
          </Grid>
        )}
      </Grid>
    </Form>
  );
};
